import styled from 'styled-components';
import { motion } from 'motion/react';
import { type ButtonVariant, type ButtonSize } from './styles';

/**
 * Versão âncora (<a>) do Button — usada para links externos (WhatsApp, etc).
 * Possui os mesmos estilos do StyledButton mas renderiza um <a> no DOM.
 */
import { css } from 'styled-components';

const base = css`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  border: 2px solid transparent;
  border-radius: ${({ theme }) => theme.radii.full};
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wide};
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  transition:
    background-color ${({ theme }) => theme.durations.fast}s ease,
    border-color ${({ theme }) => theme.durations.fast}s ease,
    color ${({ theme }) => theme.durations.fast}s ease,
    box-shadow ${({ theme }) => theme.durations.fast}s ease,
    transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);

  outline: none;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.semantic.brand.accent};
    outline-offset: 3px;
  }
`;

export const ButtonAnchor = styled(motion.a)<{
  $variant?: ButtonVariant;
  $size?: ButtonSize;
}>`
  ${base}

  /* size */
  height: ${({ $size = 'md' }) => ($size === 'sm' ? '36px' : $size === 'lg' ? '58px' : '48px')};
  padding: 0
    ${({ $size = 'md', theme }) =>
      $size === 'sm' ? theme.spacing[4] : $size === 'lg' ? theme.spacing[8] : theme.spacing[6]};
  font-size: ${({ $size = 'md', theme }) =>
    $size === 'sm'
      ? theme.fontSizes.sm
      : $size === 'lg'
        ? theme.fontSizes.lg
        : theme.fontSizes.base};

  /* variant */
  ${({ $variant = 'primary', theme }) => {
    if ($variant === 'primary')
      return css`
        background-color: ${theme.semantic.brand.accent};
        color: ${theme.colors.navy[900]};
        box-shadow: ${theme.shadows.sm};
        @media (hover: hover) {
          &:hover {
            background-color: ${theme.colors.cyan[300]};
            box-shadow: ${theme.shadows.glow};
          }
        }
      `;
    if ($variant === 'secondary')
      return css`
        background-color: ${theme.semantic.brand.primary};
        color: ${theme.semantic.text.inverse};
        box-shadow: ${theme.shadows.sm};
        @media (hover: hover) {
          &:hover {
            background-color: ${theme.semantic.brand.primaryHover};
          }
        }
      `;
    if ($variant === 'outline')
      return css`
        color: ${theme.semantic.brand.accent};
        border-color: ${theme.semantic.brand.accent};
        @media (hover: hover) {
          &:hover {
            background-color: ${theme.semantic.brand.accent};
            color: ${theme.colors.navy[900]};
          }
        }
      `;
    return css`
      color: ${theme.semantic.text.inverse};
      @media (hover: hover) {
        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
      }
    `;
  }}
  @media (hover: hover) {
    &:hover {
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0px);
    }
  }
`;
